/**
 * WeekView — the full academic weekly report. Renders, top-to-bottom:
 *
 *   ┌─ view title (学术周报 · YYYY-MM-DD 至 YYYY-MM-DD · 第 N 篇文献)
 *   ├─ overall_interpretation paragraphs
 *   ├─ 4 disease sections (肺癌 / 纵隔肿瘤 / 食管癌 / 气胸, 胸部外伤, 肋骨骨折, 胸壁畸形)
 *   ├─ excluded records accordion (collapsed default)
 *   ├─ AMA reference list
 *   └─ status / error / empty rows
 *
 * Convention compliance
 * ─────────────────────
 * - #4: the 4th section's heading reads `气胸` and its subtitle reads
 *   the full string `气胸、胸部外伤、肋骨骨折、胸壁畸形` — never
 *   collapsed or renamed.
 * - #5: NO TopFilter chip strip is rendered inside this view.
 * - #6: included-item titles render EN + ZH side by side; the same
 *   goes for the overall interpretation paragraphs (which are ZH-only
 *   by design).
 * - #11: ref_no is global within a week; we render the items in the
 *   order the worker returned them (already ref_no sorted) so the
 *   AMA list + table row numbers stay aligned.
 *
 * Caching
 * ───────
 * The view consults the store cache first. On a hit it skips the fetch
 * and reads synchronously; on a miss it triggers the fetch and stores
 * the result. The render function itself is synchronous — the caller
 * (`main.ts`) does not need to await it.
 */

import { clear, el, setChildren } from "../dom";
import { ApiError, fetchWeek } from "../api";
import { setCacheMapEntry, getStore } from "../store";
import { weekSection, DISEASE_ORDER, PNEUMOTHORAX_SUBTITLE } from "../components/weekSection";
import { excludedAccordion } from "../components/excludedAccordion";
import { amaReferenceList } from "../components/amaReferenceList";
import type {
  DiseaseKey,
  TypeLabel,
  Week,
  WeekIncludedItem,
} from "../types";

export function renderWeekView(id: string): HTMLElement {
  const root = el("section", { class: "view view-week", "data-view": "week" }, [
    el("div", { class: "view-title" }, [
      el("h1", null, "学术周报"),
      el("p", { class: "view-title-sub" }, `正在加载 ${id}…`),
    ]),
    el("div", { class: "status-row" }, [
      el("div", { class: "spinner", "aria-hidden": "true" }),
      el("div", { class: "status-row-title" }, "正在加载周报…"),
    ]),
  ]);

  void load();
  return root;

  async function load(): Promise<void> {
    // Cache fast path.
    const cached = getStore().cache.week?.[id];
    if (cached) {
      paint(cached);
      return;
    }

    try {
      const data = await fetchWeek(id);
      setCacheMapEntry("week", id, data);
      paint(data);
    } catch (err) {
      const error = err instanceof ApiError ? err : null;
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "学术周报"),
          el("p", { class: "view-title-sub" }, `无法加载 ${id}`),
        ]),
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "无法加载"),
          el(
            "div",
            { class: "status-row-detail" },
            error ? `${error.status} · ${error.message}` : "网络错误，请稍后重试。",
          ),
        ]),
      ]);
    }
  }

  function paint(data: Week): void {
    if (data.last_error && data.sections.length === 0) {
      setChildren(root, [
        el("div", { class: "view-title" }, [
          el("h1", null, "学术周报"),
          el("p", { class: "view-title-sub" }, `本周报生成失败 (${data.id})`),
        ]),
        el("div", { class: "status-row is-error" }, [
          el("div", { class: "status-row-title" }, "周报生成失败"),
          el("div", { class: "status-row-detail" }, data.last_error ?? "未知错误"),
        ]),
      ]);
      return;
    }

    const subtitle =
      `${data.start_date} 至 ${data.end_date} · 共 ${data.included_count} 篇文献纳入`;

    const title = el("div", { class: "view-title" }, [
      el("h1", null, "学术周报"),
      el("p", { class: "view-title-sub" }, subtitle),
    ]);

    const body = el("div", { class: "main-pane view-body" });

    // Overall interpretation block — one paragraph per item, ordered.
    if (data.overall_interpretation.length > 0) {
      const overall = el("section", { class: "week-overall" }, [
        el("h2", { class: "week-overall-heading" }, "总体解读"),
        el("div", { class: "week-overall-body" }, [
          ...data.overall_interpretation.map((p) =>
            el("p", { class: "week-overall-paragraph" }, compactCitations(p)),
          ),
        ]),
      ]);
      body.appendChild(overall);
    }

    // 4 disease sections in fixed order.
    const itemsByType = groupItemsByType(data.included_items);
    const diseaseSubtitles: Record<DiseaseKey, string> = {
      lung_cancer: "肺癌（含肺结节、肺良性肿瘤等）",
      mediastinal_tumor: "纵隔肿瘤（含胸腺瘤、畸胎瘤等）",
      esophageal_cancer: "食管癌（含食管良性肿瘤等）",
      pneumothorax: PNEUMOTHORAX_SUBTITLE,
      mediastinal_tumor_supplement: "纵隔肿瘤（补充）",
    };

    for (const dk of DISEASE_ORDER) {
      body.appendChild(
        weekSection({
          disease_key: dk,
          disease_subtitle: diseaseSubtitles[dk],
          items_by_type: itemsByType,
          sections: data.sections,
        }),
      );
    }

    // Excluded records appendix (collapsed default).
    body.appendChild(
      excludedAccordion({ records: data.excluded_records }),
    );

    // AMA reference list.
    body.appendChild(
      amaReferenceList({ items: data.included_items }),
    );

    clear(root);
    root.appendChild(title);
    root.appendChild(body);
  }
}

function groupItemsByType(items: WeekIncludedItem[]): Record<TypeLabel, WeekIncludedItem[]> {
  const out: Record<TypeLabel, WeekIncludedItem[]> = {
    "临床研究": [],
    "人工智能/机器学习相关研究": [],
    "其他基础研究": [],
  };
  for (const it of items) {
    out[it.type_label].push(it);
  }
  return out;
}

function compactCitations(text: string): string {
  if (!text) return "";
  return text
    .replace(/、\s+/gu, "、")
    .replace(/\s+、/gu, "、")
    .replace(/\[\s+/gu, "[")
    .replace(/\s+\]/gu, "]")
    .replace(/、\]/gu, "]")
    .replace(/\]\s*\[/gu, "][")
    .trim();
}